import React, { useState, useEffect } from "react";
import "./DisplayPreferences.css";
import axios from "axios";
import openNotification from "components/utils/CustomNotification";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

const DisplayPreferences: React.FC = () => {
  useCheckSessionHeartBeat();
  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";
  const org_uid = localStorage.getItem("org_uid") || "";

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [userType, setUserType] = useState("");

  const [selectedUnits, setSelectedUnits] = useState({
    steps_metric_unit: "",
    distance_metric_unit: "",
    caloriesburned_metric_unit: "",
    weight_unit: "",
    height_unit: "",
    waist_size_unit: "",
    chest_size_unit: "",
  });

  const [originalUnits, setOriginalUnits] = useState({
    steps_metric_unit: "",
    distance_metric_unit: "",
    caloriesburned_metric_unit: "",
    weight_unit: "",
    height_unit: "",
    waist_size_unit: "",
    chest_size_unit: "",
  });

  const [unitdata, setUnitdata] = useState<{
    [key: string]: { value: string; label: string }[];
  } | null>(null);

  const fetchunitdata = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${beAddress}/api/get-unit-map`, {
        headers: {
          "Content-Type": "application/json",
          "user-uid": userUid,
        }, withCredentials: true,
      });

      if (
        response.data.status === "Success" &&
        typeof response.data.data === "object"
      ) {
        setUnitdata(response.data.data);
      } else {
        setErrorMessage(response.data.message);
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(
          error.response?.data?.message || "Failed to fetch unit data"
        );
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("An unknown error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await fetch(`${beAddress}/api/user-profile`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "user-uid": userUid,
          }, credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Failed to fetch user profile");
        }

        const result = await response.json();

        if (result.status === "Success" && typeof result.data === "object") {
          const userData = result.data;
          setUserType(userData.user_type || "");

          const units = {
            steps_metric_unit: userData.steps_metric_unit || "count",
            distance_metric_unit: userData.distance_metric_unit || "meters",
            caloriesburned_metric_unit:
              userData.caloriesburned_metric_unit || "kilocalories",
            weight_unit: userData.weight_unit || "kg",
            height_unit: userData.height_unit || "cm",
            waist_size_unit: userData.waist_size_unit || "inch",
            chest_size_unit: userData.chest_size_unit || "inch",
          };

          setSelectedUnits(units);
          setOriginalUnits(units);
        } else {
          console.error("Invalid data format from user-profile API");
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUserProfile();
    fetchunitdata();
  }, []);

  const handleupdateunits = async () => {
    const updatedPreferences: Record<string, string> = {};

    for (const key in selectedUnits) {
      const typedKey = key as keyof typeof selectedUnits;

      if (selectedUnits[typedKey] !== originalUnits[typedKey]) {
        updatedPreferences[typedKey] = selectedUnits[typedKey];
      }
    }

    if (Object.keys(updatedPreferences).length === 0) {
      openNotification({
        type: "info",
        message: "No changes made to display preferences.",
      });
      return;
    }

    updatedPreferences.user_uid = userUid;
    updatedPreferences.user_type = userType;
    updatedPreferences.org_uid = org_uid;

    try {
      const response = await axios.put(
        `${beAddress}/api/user-profile`,
        updatedPreferences,
        {
          headers: {
            "Content-Type": "application/json",
            "user-uid": userUid,
          }, withCredentials: true,
        }
      );

      if (response.data.status === "Success") {
        openNotification({
          type: "success",
          message: "Display preferences updated successfully!",
        });

        setOriginalUnits({ ...selectedUnits });
      } else {
        openNotification({
          type: "error",
          message: response.data.message || "Update failed.",
        });
      }
    } catch (error) {
      openNotification({
        type: "error",
        message: "Error updating display preferences.",
      });
      console.error("Error updating display preferences:", error);
    }
  };

  return (
    <div className="display-preferences-container">
      <div className="display-preferences-content">
        <div className="display-preferences-header">
        </div>

        <div className="preferences-card">
          {loading ? (
            <div className="loading-state">Loading preferences...</div>
          ) : errorMessage ? (
            <div className="error-state">{errorMessage}</div>
          ) : (
            <div className="preferences-grid">
              {unitdata &&
                Object.entries(unitdata).map(([unitKey, options]) => (
                  <div key={unitKey} className="preference-item">
                    <label className="preference-label">
                      {unitKey
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                    </label>
                    <select
                      className="preference-select"
                      value={
                        selectedUnits[unitKey as keyof typeof selectedUnits]
                      }
                      onChange={(e) =>
                        setSelectedUnits((prev) => ({
                          ...prev,
                          [unitKey]: e.target.value,
                        }))
                      }
                    >
                      {options.map((unit: { label: string; value: string }) => (
                        <option key={unit.value} value={unit.value}>
                          {unit.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
            </div>
          )}

          <div className="preferences-actions">
            <button
              className="save-preferences-btn"
              onClick={handleupdateunits}
              disabled={loading}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisplayPreferences;
